"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.DmsTableDeleteButton = void 0;
var _react = _interopRequireWildcard(require("react"));
var _reactIntlUniversal = _interopRequireDefault(require("react-intl-universal"));
var _Dialog = _interopRequireDefault(require("@mui/material/Dialog"));
var _DialogTitle = _interopRequireDefault(require("@mui/material/DialogTitle"));
var _DialogContent = _interopRequireDefault(require("@mui/material/DialogContent"));
var _DialogActions = _interopRequireDefault(require("@mui/material/DialogActions"));
var _Typography = _interopRequireDefault(require("@mui/material/Typography"));
var _DmsButton = require("../DmsButton/DmsButton");
var _DmsTableButton = require("./DmsTableButton");
var _Delete = _interopRequireDefault(require("@mui/icons-material/Delete"));
var _DmsTokenHook = require("../DmsTokenHook/DmsTokenHook");
var _HttpStatus = require("../../constants/HttpStatus");
var _errors = require("../../service/errors/errors");
var _DmsFetch = require("../DmsFetch/DmsFetch");
var _sweetalert = _interopRequireDefault(require("sweetalert2"));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function (e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, default: e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (const t in e) "default" !== t && {}.hasOwnProperty.call(e, t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, t)) && (i.get || i.set) ? o(f, t, i) : f[t] = e[t]); return f; })(e, t); }
/**
 * DmsTableDeleteButton component renders a delete button and handles the delete operation for a row in the table.
 * It shows a confirmation modal before performing the delete operation, and also handles errors via an error modal.
 *
 * @param {ReactNode} button_icon - Icon to display on the button, defaults to a Delete icon.
 * @param {number|string} row_pk - Primary key of the row to be deleted.
 * @param {function} [handler] - Optional custom delete handler function.
 * @param {boolean} [needs_authentication] - Whether the request requires authentication. If true, the token is sent in the Authorization header.
 * @param {string} [text="Are you sure about it?"] - Confirmation message to show in the modal.
 * @param {string} [title="Delete"] - Title of the confirmation modal.
 * @param {string} url - Endpoint URL to perform the delete request.
 * @param {object} table - Reference to the table component to update after deletion.
 * @param {string} [button_label] - Optional label for the button.
 * @param {string} [button_text] - Optional text for the button.
 * @param {string} [queryData] - Optional query string to append to the URL.
 * @param {string} [variant="danger"] - Button variant type (e.g., 'danger', 'secondary').
 * @param {boolean} [disabled=false] - Whether the button is disabled.
 * @returns {ReactElement} JSX for the delete button, modal, and error handling.
 */

const DmsTableDeleteButton = _ref => {
  let {
    button_icon = /*#__PURE__*/_react.default.createElement(_Delete.default, null),
    row_pk,
    handler,
    needs_authentication,
    text = _reactIntlUniversal.default.get("Areyousureaboutit?").d("Are you sure about it?"),
    title = _reactIntlUniversal.default.get("Delete").d("Delete"),
    url,
    table,
    button_label,
    button_text,
    queryData,
    variant = "danger",
    disabled = false
  } = _ref;
  const [modalOpen, setModalOpen] = (0, _react.useState)(false);
  const [errorModalOpen, setErrorModalOpen] = (0, _react.useState)(false);
  const [errorText, setErrorText] = (0, _react.useState)("");
  const {
    token
  } = (0, _DmsTokenHook.useToken)();
  const syncData = (0, _react.useRef)({});
  const handleButton = async () => {
    setModalOpen(true);
  };
  const handleDelete = async () => {
    if (handler) {
      handler(row_pk);
    } else if (url) {
      var _table$current;
      if (!needs_authentication && table && table.current) {
        needs_authentication = table.current.needs_authentication;
      }
      let endpoint = url;
      if (!endpoint.endsWith("/")) {
        endpoint += "/";
      }
      endpoint += row_pk;
      if (queryData) {
        endpoint += "?" + queryData;
      }
      const res = await (0, _DmsFetch.fetchData)(endpoint, "DELETE", needs_authentication ? true : null, syncData.current, true, true);
      if (res.status !== _HttpStatus.HTTP_STATUS.OK_200) {
        var _res$data, _res$data2;
        setErrorText((0, _errors.getErrorDetail)(((_res$data = res.data) === null || _res$data === void 0 ? void 0 : _res$data.detail) || ((_res$data2 = res.data) === null || _res$data2 === void 0 ? void 0 : _res$data2.reason) || _reactIntlUniversal.default.get("Unknownerror").d("Unknown error"))); //remove data from detail
        setErrorModalOpen(true);
      } else if (table !== null && table !== void 0 && (_table$current = table.current) !== null && _table$current !== void 0 && _table$current.updateTable) {
        _sweetalert.default.fire({
          icon: "success",
          title: _reactIntlUniversal.default.get("Success").d("Success")
        });
        table.current.updateTable();
      }
    }
    setModalOpen(false);
  };
  const modalContent = /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.createElement(_Dialog.default, {
    open: modalOpen,
    onClose: () => setModalOpen(false),
    "aria-labelledby": "modal-title",
    "aria-describedby": "modal-description",
    maxWidth: "sm",
    fullWidth: true
  }, /*#__PURE__*/_react.default.createElement(_DialogTitle.default, {
    id: "modal-title"
  }, title), /*#__PURE__*/_react.default.createElement(_DialogContent.default, null, /*#__PURE__*/_react.default.createElement(_Typography.default, {
    id: "modal-description"
  }, text)), /*#__PURE__*/_react.default.createElement(_DialogActions.default, null, /*#__PURE__*/_react.default.createElement(_DmsButton.DmsButton, {
    variant: "secondary",
    text: _reactIntlUniversal.default.get("Cancel").d("Cancel"),
    onClick: () => setModalOpen(false)
  }), /*#__PURE__*/_react.default.createElement(_DmsButton.DmsButton, {
    variant: "danger",
    onClick: handleDelete,
    text: _reactIntlUniversal.default.get("Delete").d("Delete")
  }))), /*#__PURE__*/_react.default.createElement(_Dialog.default, {
    open: errorModalOpen,
    onClose: () => setErrorModalOpen(false),
    "aria-labelledby": "error-modal-title",
    "aria-describedby": "error-modal-description",
    maxWidth: "sm",
    fullWidth: true
  }, /*#__PURE__*/_react.default.createElement(_DialogTitle.default, {
    id: "error-modal-title"
  }, _reactIntlUniversal.default.get("Error").d("Error")), /*#__PURE__*/_react.default.createElement(_DialogContent.default, null, /*#__PURE__*/_react.default.createElement(_Typography.default, {
    id: "error-modal-description"
  }, errorText)), /*#__PURE__*/_react.default.createElement(_DialogActions.default, null, /*#__PURE__*/_react.default.createElement(_DmsButton.DmsButton, {
    variant: "secondary",
    text: _reactIntlUniversal.default.get("Ok").d("Ok"),
    onClick: () => setErrorModalOpen(false)
  }))));
  return /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.createElement(_DmsTableButton.DmsTableButton, {
    table: table,
    variant: variant,
    text: button_text,
    label: button_label,
    icon: button_icon,
    disabled: disabled,
    onClick: handleButton,
    title: _reactIntlUniversal.default.get("Tooltip.Action.Delete").d("Delete")
  }), modalContent);
};
exports.DmsTableDeleteButton = DmsTableDeleteButton;